import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Archive, Plus, Trash2 } from "lucide-react";
import {
  commissionsApi,
  type RevenuePoolDto,
  type UpsertPoolDescriptionRequest,
  type UpsertRevenuePoolRequest,
} from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Badge, Button, Card, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { UnallocatedPoolNotice } from "@/components/commissions/UnallocatedPoolNotice";

export function PoolAdminPage() {
  const pools = useQuery({
    queryKey: commissionKeys.poolList("all"),
    queryFn: () => commissionsApi.listPools(true),
  });
  const [editing, setEditing] = useState<RevenuePoolDto | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="flex flex-col gap-space-5">
      <div className="flex items-end justify-between gap-space-3">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Commissions · Admin
          </div>
          <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
            Revenue pools
          </h1>
          <p className="text-body text-gray-mid mt-space-2 max-w-reading">
            Pools are categories, not fee inventories. Retire a pool to stop new claims without
            removing history.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus size={14} strokeWidth={2} aria-hidden /> New pool
        </Button>
      </div>

      <UnallocatedPoolNotice />

      {pools.isLoading ? (
        <div className="font-display text-label uppercase tracking-label text-gray-mid">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
          {pools.data?.map((p) => (
            <Card key={p.id} className="flex flex-col gap-space-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-space-2">
                    <span className="font-display text-h3 font-bold text-onyx">{p.code}</span>
                    {p.isRetired && <Badge status="archived">Retired</Badge>}
                  </div>
                  <div className="font-display font-bold text-body text-onyx mt-space-1">{p.name}</div>
                </div>
                <div className="text-caption text-gray-mid">{p.claimCount} claims</div>
              </div>
              {p.description && (
                <p className="text-small text-gray-mid m-0">{p.description}</p>
              )}
              {p.exclusions && (
                <div className="p-space-2 border border-status-warning/50 rounded-md bg-status-warning/5 text-caption">
                  <span className="font-display text-[10px] font-semibold uppercase tracking-label text-status-warning">
                    Exclusions
                  </span>
                  <div className="text-onyx mt-space-1">{p.exclusions}</div>
                </div>
              )}
              <div className="text-caption text-gray-mid">
                {p.suggestedDescriptions.length} suggested description
                {p.suggestedDescriptions.length === 1 ? "" : "s"}
              </div>
              <div className="flex gap-space-2 pt-space-2 border-t border-border-light">
                <Button variant="secondary" size="sm" onClick={() => setEditing(p)}>
                  Edit
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {creating && (
        <PoolEditor
          pool={null}
          onClose={() => setCreating(false)}
        />
      )}
      {editing && <PoolEditor pool={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

interface EditorProps {
  pool: RevenuePoolDto | null;
  onClose: () => void;
}

function PoolEditor({ pool, onClose }: EditorProps) {
  const qc = useQueryClient();
  const [code, setCode] = useState(pool?.code ?? "");
  const [name, setName] = useState(pool?.name ?? "");
  const [description, setDescription] = useState(pool?.description ?? "");
  const [exclusions, setExclusions] = useState(pool?.exclusions ?? "");
  const [sortOrder, setSortOrder] = useState<number>(pool?.sortOrder ?? 99);
  const [error, setError] = useState<string | null>(null);

  const [descDraft, setDescDraft] = useState("");

  const save = useMutation({
    mutationFn: () => {
      const payload: UpsertRevenuePoolRequest = {
        code: code.trim(),
        name: name.trim(),
        description: description.trim() || null,
        exclusions: exclusions.trim() || null,
        sortOrder,
      };
      return pool ? commissionsApi.updatePool(pool.id, payload) : commissionsApi.createPool(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.pools() });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const retire = useMutation({
    mutationFn: () => commissionsApi.retirePool(pool!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.pools() });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const addDescription = useMutation({
    mutationFn: (body: UpsertPoolDescriptionRequest) =>
      commissionsApi.addDescription(pool!.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.pools() });
      setDescDraft("");
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const removeDescription = useMutation({
    mutationFn: (descriptionId: string) =>
      commissionsApi.removeDescription(pool!.id, descriptionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: commissionKeys.pools() }),
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const canSave = code.trim().length > 0 && name.trim().length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={pool ? `Edit pool ${pool.code}` : "Create pool"}
      size="lg"
      footer={
        <>
          {pool && !pool.isRetired && (
            <Button
              variant="destructive"
              loading={retire.isPending}
              onClick={() => retire.mutate()}
            >
              <Archive size={14} strokeWidth={1.5} aria-hidden /> Retire
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSave}
            loading={save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      {error && (
        <div role="alert" className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small mb-space-3">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      )}
      <div className="flex flex-col gap-space-3">
        <div className="grid grid-cols-[120px_1fr] gap-space-3">
          <FormField label="Code" required>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={5} />
          </FormField>
          <FormField label="Name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
        </div>
        <FormField label="Description">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
          />
        </FormField>
        <FormField label="Exclusions" helperText="What does NOT belong in this pool.">
          <textarea
            value={exclusions}
            onChange={(e) => setExclusions(e.target.value)}
            rows={2}
            className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
          />
        </FormField>
        <FormField label="Sort order">
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
          />
        </FormField>

        {pool && (
          <div className="border-t border-border-light pt-space-3 mt-space-2">
            <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
              Suggested descriptions
            </div>
            {pool.suggestedDescriptions.length === 0 && (
              <div className="text-small text-gray-mid">None yet.</div>
            )}
            <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
              {pool.suggestedDescriptions
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-space-2 p-space-2 border border-border-light rounded-md"
                  >
                    <span className="text-small">{s.description}</span>
                    <button
                      type="button"
                      onClick={() => removeDescription.mutate(s.id)}
                      className="p-space-1 rounded-full text-gray-mid hover:text-status-error"
                      aria-label="Remove"
                    >
                      <Trash2 size={14} strokeWidth={1.5} aria-hidden />
                    </button>
                  </li>
                ))}
            </ul>
            <div className="flex gap-space-2 mt-space-3">
              <Input
                value={descDraft}
                onChange={(e) => setDescDraft(e.target.value)}
                placeholder="Add a suggestion…"
              />
              <Button
                variant="secondary"
                size="sm"
                disabled={descDraft.trim().length === 0 || addDescription.isPending}
                loading={addDescription.isPending}
                onClick={() =>
                  addDescription.mutate({
                    description: descDraft.trim(),
                    sortOrder: (pool.suggestedDescriptions.at(-1)?.sortOrder ?? 0) + 1,
                    isActive: true,
                  })
                }
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
